import { Box, Collapse, Stack, Typography, useTheme } from "@mui/material";
import { Button } from "components/button";
import { DownArrowIcon } from "components/icons";
import { Fragment, useState } from "react";
import type {
  AskHelenaPendingWrite,
  AskHelenaPendingWriteStatus,
} from "shared";

const RESOLVED_STATUS_LABEL: Record<AskHelenaPendingWriteStatus, string> = {
  pending: "No longer available",
  confirmed: "Approved",
  cancelled: "Declined",
  expired: "Expired",
  superseded: "Replaced by your reply",
};

/** Arguments are `unknown` on the wire; anything non-scalar is shown as JSON. */
const formatArgumentValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  return JSON.stringify(value);
};

interface AskHelenaConfirmCardProps {
  pendingWrite: AskHelenaPendingWrite;
  /**
   * Approve or decline has been used, or the proposal lapsed. The buttons go
   * rather than merely disabling — removing them is a stronger guarantee
   * against a second click than any `disabled` attribute, and the status row
   * below already reports that the turn resumed.
   */
  isResolved?: boolean;
  onApprove: () => void;
  onDecline: () => void;
}

/**
 * The approval card — the control that stands between an instruction injected
 * into a ticket body and a real change to a customer's data.
 *
 * Helena reads ticket text, forwarded email and uploaded files, all written by
 * outsiders. The service is built so that such text can make her PROPOSE a
 * change but never MAKE one; proposing and executing are separated by this
 * click. The residual risk is therefore social — a plausible-looking card
 * approved without being read — which is why `summary` is rendered verbatim and
 * given the most visual weight, and why nothing here is ever auto-confirmed.
 */
export const AskHelenaConfirmCard = ({
  pendingWrite,
  isResolved = false,
  onApprove,
  onDecline,
}: AskHelenaConfirmCardProps) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(true);

  const argumentEntries = Object.entries(pendingWrite.arguments);

  return (
    <Stack
      sx={{
        alignSelf: "flex-start",
        width: "100%",
        maxWidth: "640px",
        gap: "20px",
        p: "24px",
        borderRadius: "12px",
        border: `1px solid ${theme.palette.neutral["015"]}`,
        backgroundColor: theme.palette.neutral.main,
        boxShadow: `0px 2px 8px -4px ${theme.palette.neutral["020"]}`,
        // A retired proposal stays as the record of what was agreed to, but
        // stops competing for attention with the answer that followed it.
        opacity: isResolved ? 0.6 : 1,
      }}
    >
      <Stack sx={{ gap: "4px" }}>
        {/* The human phrase. The registry identifier (`name`) is never rendered
            — it is for correlation and logging only. */}
        <Typography
          variant="body-xsmall"
          sx={{ color: theme.palette.primitive.system.violet[700] }}
        >
          {pendingWrite.label}
        </Typography>
        {/* VERBATIM. This is the exact text the approval is recorded against;
            rebuilding the sentence from `arguments` would mean the user
            approves one thing and the service records another. It carries the
            heaviest type in the card for the same reason. */}
        <Typography
          variant="subtitle1"
          sx={{ color: theme.palette.lightBg.high }}
        >
          {pendingWrite.summary}
        </Typography>
      </Stack>

      {argumentEntries.length > 0 && (
        <Stack
          sx={{
            borderRadius: "8px",
            border: `1px solid ${theme.palette.neutral["010"]}`,
            backgroundColor: theme.palette.neutral["005"],
          }}
        >
          <Stack
            component="button"
            type="button"
            direction="row"
            onClick={() => setIsExpanded((expanded) => !expanded)}
            aria-expanded={isExpanded}
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              p: "16px 20px",
              border: "none",
              // Only a separator while the rows below it are actually showing.
              borderBottom: isExpanded
                ? `1px solid ${theme.palette.neutral["015"]}`
                : "none",
              backgroundColor: "transparent",
              cursor: "pointer",
              color: theme.palette.lightBg.low,
              textAlign: "left",
            }}
          >
            <Typography variant="caption-3" color="inherit">
              Details
            </Typography>
            <DownArrowIcon
              sx={{
                fontSize: "20px",
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          </Stack>

          <Collapse in={isExpanded} unmountOnExit>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "max-content minmax(0, 1fr)",
                columnGap: "12px",
                rowGap: "8px",
                alignItems: "flex-start",
                p: "12px",
              }}
            >
              {argumentEntries.map(([key, value]) => (
                <Fragment key={key}>
                  <Typography
                    variant="body-medium"
                    sx={{ color: theme.palette.lightBg.high }}
                  >
                    {`${key}:`}
                  </Typography>
                  <Typography
                    variant="body-medium"
                    sx={{
                      color: theme.palette.lightBg.high,
                      wordBreak: "break-word",
                    }}
                  >
                    {formatArgumentValue(value)}
                  </Typography>
                </Fragment>
              ))}
            </Box>
          </Collapse>
        </Stack>
      )}

      {isResolved ? (
        pendingWrite.status ? (
          <Typography
            variant="caption-3"
            sx={{
              alignSelf: "flex-end",
              color: theme.palette.lightBg.low,
            }}
          >
            {RESOLVED_STATUS_LABEL[pendingWrite.status]}
          </Typography>
        ) : null
      ) : (
        <Stack
          direction="row"
          sx={{ gap: "8px", justifyContent: "flex-end", flexWrap: "wrap" }}
        >
          {/* Decline comes first in the DOM so Approve is never what a stray
              Enter or an autofocus lands on. Approving must be a deliberate,
              aimed click. */}
          <Button
            variant="secondary"
            onClick={onDecline}
            sx={{
              // The size preset pads asymmetrically (it reserves room for an
              // icon slot neither button uses).
              borderColor: theme.palette.primitive.system.violet[800],
              color: theme.palette.primitive.system.violet[800],
              "&:hover": {
                borderColor: theme.palette.primitive.system.violet[700],
                backgroundColor: theme.palette.primitive.system.violet[25],
                color: theme.palette.primitive.system.violet[700],
              },
            }}
          >
            Decline
          </Button>
          <Button
            variant="primary"
            onClick={onApprove}
            sx={{
              backgroundColor: theme.palette.primitive.system.violet[800],
              borderColor: theme.palette.primitive.system.violet[800],
              "&:hover": {
                backgroundColor: theme.palette.primitive.system.violet[700],
                borderColor: theme.palette.primitive.system.violet[700],
              },
            }}
          >
            Approve
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
